import { Controller } from "@hotwired/stimulus"

// ---------------------------------------------------------------------------
// Vendor-intake brochure wizard controller.
//
// Drives the 5-step brochure builder (build.html.erb) and the embedded live
// preview (_brochure_doc.html.erb). It owns ONE state object and, on every
// change, (a) patches the in-page brochure via its [data-vbr] hooks + the
// .vbr-doc data-attributes / CSS vars, (b) keeps the hidden form inputs in
// sync, and (c) re-measures the one-page fit meter. The theme / layout /
// color / font pickers and preset buttons are rendered client-side from the
// serialized BrochureTheme.payload (vocabValue) — the single source of truth.
//
// It never rebuilds the brochure's layout markup: every layout / rateStyle /
// graphic variant already lives in the DOM server-side, gated by CSS on the
// doc's data-attributes, so the Ferrum PDF == this preview.
// ---------------------------------------------------------------------------
export default class extends Controller {
  static targets = [
    "form",
    // step nav
    "stepBtn", "step", "backBtn", "nextBtn",
    // hidden fields kept in sync with state
    "themeField", "colorField", "logoScaleField", "layoutField",
    "orientationField", "fontField", "styleField", "sectionsField", "locationsField",
    // category
    "otherWrap", "otherInput",
    // locations
    "locList", "locRow", "locTemplate", "locAddBtn", "locCount",
    // services
    "svcList", "svcRow", "svcTemplate", "addBtn", "svcCount",
    // logo
    "logoDrop", "logoInput", "cropWrap", "cropZoom", "cropX", "cropY", "cropSize", "knockout",
    // sections
    "sectionToggle", "sectionBody",
    // style pickers
    "themes", "layouts", "colors", "fonts", "orientSeg", "orientHint",
    // review
    "checklist", "agree", "submitBtn", "downloadBtn",
    // preview
    "badge", "previewFrame", "fit", "fitText"
  ]

  static values = {
    vocab: Object,
    themes: Array,
    theme: String,
    color: String,
    layout: String,
    orientation: String,
    font: String,
    style: Object,
    sections: Object,
    locations: Array,
    logoScale: String,
    sample: Boolean,
    max: Number,
    step: Number
  }

  // ---- lifecycle --------------------------------------------------------

  connect() {
    this.doc = this.element.querySelector(".vbr-doc")

    // ONE state object. `style.logo` holds the crop/plate controls; the
    // top-level style also carries the theme's rateStyle/graphic (left blank
    // so the theme's own defaults win until the vendor overrides them).
    const st = this._obj(this.styleValue)
    this.state = {
      org: { name: "", contact: "", tagline: "", phone: "", email: "", website: "", address: "", notes: "", category: "" },
      locations: Array.isArray(this.locationsValue) ? this.locationsValue.slice() : [],
      services: [],
      sections: this._normSections(this.sectionsValue),
      style: {
        theme: this.themeValue,
        layout: this.layoutValue,
        color: this.colorValue,
        font: this.fontValue,
        orient: this.orientationValue,
        rateStyle: st.rateStyle || "",
        graphic: st.graphic || "",
        logo: {
          dataUri: null,
          fit: st.fit || "contain",
          zoom: this._num(st.zoom, 100),
          x: this._num(st.x, 50),
          y: this._num(st.y, 50),
          plate: st.plate || "transparent",
          knockout: st.knockout === true || st.knockout === "true" || st.knockout === "1",
          size: this._num(this.logoScaleValue ? parseFloat(this.logoScaleValue) * 100 : 100, 100)
        }
      }
    }
    this._orientTouched = false   // vendor manually changed orientation?
    this._logoAspect = null

    this._renderThemes()
    this._renderLayouts()
    this._renderColors()
    this._renderFonts()
    this._syncOrientSeg()
    this._syncSegments()
    this._updateSvcCount()
    this._updateLocCount()
    this._syncHidden()

    // Apply the current style to the doc immediately (theme/color/etc. may
    // differ from the server-rendered defaults after a validation re-render).
    this._paintStyle()

    // Leave the server-rendered sample in place until the first edit.
    this.showStep(this.stepValue || 1)
    this._measureSoon()
    window.addEventListener("resize", this._onResize)
  }

  disconnect() {
    window.removeEventListener("resize", this._onResize)
  }

  _onResize = () => this._measureSoon()

  // ---- step navigation --------------------------------------------------

  goStep(e) {
    this.showStep(parseInt(e.currentTarget.dataset.step, 10) || 1)
  }

  next() {
    this.showStep(Math.min((this.stepValue || 1) + 1, 5))
  }

  back() {
    this.showStep(Math.max((this.stepValue || 1) - 1, 1))
  }

  showStep(n) {
    this.stepValue = n
    this.stepTargets.forEach(p => { p.hidden = parseInt(p.dataset.step, 10) !== n })
    this.stepBtnTargets.forEach(b => {
      const bn = parseInt(b.dataset.step, 10)
      b.classList.toggle("is-active", bn === n)
      b.classList.toggle("is-done", bn < n)
    })
    if (this.hasBackBtnTarget) this.backBtnTarget.hidden = n <= 1
    if (this.hasNextBtnTarget) this.nextBtnTarget.hidden = n >= 5
    if (n === 5) this._refreshChecklist()
    this._measureSoon()
  }

  // ---- Step 1: org text fields -----------------------------------------

  field(e) {
    this._exitSample()
    const key = e.target.dataset.field || e.target.dataset.secField
    const val = e.target.value
    switch (key) {
      case "name":    this.state.org.name = val; this._text("name", val.trim() || "Your business name"); break
      case "contact": this.state.org.contact = val; break
      case "tagline": this.state.org.tagline = val; this._subline(); break
      case "phone":   this.state.org.phone = val; this._contact("phone", val); break
      case "email":   this.state.org.email = val; this._contact("email", val); this._footer(); break
      case "website": this.state.org.website = val; this._contact("website", val); this._footer(); break
      case "address": this.state.org.address = val; this._paintLocations(); break
      case "about":   this.state.org.notes = val; this._paintSections(); break
    }
    this._measureSoon()
  }

  updateCategory() {
    this._exitSample()
    const sel   = this.element.querySelector('[name="service_category"]')
    const other = sel && sel.value === "Other"
    if (this.hasOtherWrapTarget) this.otherWrapTarget.hidden = !other
    const val = other
      ? (this.hasOtherInputTarget ? this.otherInputTarget.value : "")
      : (sel ? sel.value : "")
    this.state.org.category = val
    // category is the brochure subline fallback when no tagline is set
    this._subline()
    this._setCategory(val)
    this._measureSoon()
  }

  // ---- Step 1: locations ------------------------------------------------

  addLocation() {
    if (this.state.locations.length >= 11) return   // + the primary address = 12
    const frag = this.locTemplateTarget.content.cloneNode(true)
    this.locListTarget.appendChild(frag)
    this._collectLocations()
    this._updateLocCount()
  }

  removeLocation(e) {
    e.currentTarget.closest("[data-vendor-intake-target='locRow']")?.remove()
    this._collectLocations()
    this._updateLocCount()
  }

  locationsChanged() {
    this._exitSample()
    this._collectLocations()
  }

  _collectLocations() {
    this.state.locations = this.locRowTargets
      .map(r => r.querySelector("input")?.value.trim() || "")
      .filter(Boolean)
    this._paintLocations()
    this._syncHidden()
    this._measureSoon()
  }

  _updateLocCount() {
    if (this.hasLocCountTarget) {
      const n = this.locRowTargets.length
      this.locCountTarget.textContent = n ? `(+${n})` : ""
    }
    if (this.hasLocAddBtnTarget) this.locAddBtnTarget.disabled = this.locRowTargets.length >= 11
  }

  // ---- Step 2: services -------------------------------------------------

  preset(e) {
    this._exitSample()
    const rows = (this.vocabValue.presets || {})[e.currentTarget.dataset.preset]
    if (!rows) return
    // Replace all current rows with the preset's.
    this.svcListTarget.innerHTML = ""
    this._svcIdx = 0
    rows.slice(0, this.maxValue).forEach(r => this._appendServiceRow(r))
    this._updateSvcCount()
    this.servicesChanged()
  }

  addService() {
    if (this.svcRowTargets.length >= this.maxValue) return
    this._appendServiceRow(null)
    this._updateSvcCount()
    this.servicesChanged()
  }

  removeService(e) {
    if (this.svcRowTargets.length <= 1) return
    e.currentTarget.closest("[data-vendor-intake-target='svcRow']")?.remove()
    this._reindexServices()
    this._updateSvcCount()
    this.servicesChanged()
  }

  moveUp(e) {
    const row = e.currentTarget.closest("[data-vendor-intake-target='svcRow']")
    const prev = row?.previousElementSibling
    if (row && prev) prev.before(row)
    this._reindexServices()
    this.servicesChanged()
  }

  moveDown(e) {
    const row = e.currentTarget.closest("[data-vendor-intake-target='svcRow']")
    const next = row?.nextElementSibling
    if (row && next) next.after(row)
    this._reindexServices()
    this.servicesChanged()
  }

  servicesChanged() {
    this._exitSample()
    this.state.services = this.svcRowTargets.map(row => ({
      name: row.querySelector('[data-svc-field="name"]')?.value.trim() || "",
      mode: row.querySelector('[data-svc-field="mode"]')?.value || "",
      rate: row.querySelector('[data-svc-field="rate"]')?.value || "",
      unit: row.querySelector('[data-svc-field="unit"]')?.value || ""
    }))
    this._paintServices()
    this._subline()
    this._syncHidden()
    this._measureSoon()
  }

  // Append a service row from the <template>, optionally filling it from a
  // preset row {name, mode, rate, unit}.
  _appendServiceRow(data) {
    if (this._svcIdx == null) this._svcIdx = this.svcRowTargets.length
    const idx = this._svcIdx++
    const html = this.svcTemplateTarget.innerHTML.replace(/__IDX__/g, String(idx))
    const tmp = document.createElement("div")
    tmp.innerHTML = html.trim()
    const row = tmp.firstElementChild
    if (data) {
      const set = (sel, v) => { const el = row.querySelector(sel); if (el != null && v != null) el.value = v }
      set('[data-svc-field="name"]', data.name)
      set('[data-svc-field="mode"]', data.mode)
      set('[data-svc-field="rate"]', data.rate)
      set('[data-svc-field="unit"]', data.unit)
    }
    this.svcListTarget.appendChild(row)
    return row
  }

  // Keep the services[N][...] input names contiguous after add/remove/reorder
  // so the server parses them in visible order.
  _reindexServices() {
    this.svcRowTargets.forEach((row, i) => {
      row.querySelectorAll("[name]").forEach(el => {
        el.name = el.name.replace(/services\[\d+\]/, `services[${i}]`)
      })
    })
    this._svcIdx = this.svcRowTargets.length
  }

  _updateSvcCount() {
    if (this.hasSvcCountTarget) this.svcCountTarget.textContent = `${this.svcRowTargets.length}/${this.maxValue}`
    if (this.hasAddBtnTarget) this.addBtnTarget.disabled = this.svcRowTargets.length >= this.maxValue
  }

  // ---- Step 3: optional sections ---------------------------------------

  toggleSection(e) {
    this._exitSample()
    const key = e.target.dataset.section
    const on = e.target.checked
    this.state.sections.enabled[key] = on
    const body = this.sectionBodyTargets.find(b => b.dataset.sectionBody === key)
    if (body) body.hidden = !on
    this._paintSections()
    this._syncHidden()
    this._measureSoon()
  }

  sectionField(e) {
    this._exitSample()
    const key = e.target.dataset.secField
    this.state.sections[key] = e.target.value
    this._paintSections()
    this._syncHidden()
    this._measureSoon()
  }

  fundingChanged() {
    this._exitSample()
    this.state.sections.funding = Array.from(
      this.element.querySelectorAll('[data-sec-field="funding"]:checked')
    ).map(cb => cb.value)
    this._paintSections()
    this._syncHidden()
    this._measureSoon()
  }

  // ---- Step 4: style pickers -------------------------------------------

  theme(e) {
    const btn = e.target.closest("[data-theme]")
    if (!btn) return
    const key = btn.dataset.theme
    const t = (this.vocabValue.themes || []).find(x => x.key === key)
    if (!t) return
    this.state.style.theme = key
    // A theme carries a full look: adopt its color, layout, font, rateStyle
    // and graphic (the vendor can still override each afterwards).
    this.state.style.color = t.color
    this.state.style.layout = t.header
    this.state.style.font = t.font
    this.state.style.rateStyle = t.rateStyle
    this.state.style.graphic = t.graphic
    this._renderThemes()
    this._renderColors()
    this._syncSegments()
    this._paintStyle()
    this._syncHidden()
    this._measureSoon()
  }

  layout(e) {
    const btn = e.target.closest("[data-layout]")
    if (!btn) return
    this.state.style.layout = btn.dataset.layout
    this._renderLayouts()
    this._paintStyle()
    this._syncHidden()
    this._measureSoon()
  }

  color(e) {
    const btn = e.target.closest("[data-color]")
    if (!btn) return
    this.state.style.color = btn.dataset.color
    this._renderColors()
    this._paintStyle()
    this._syncHidden()
    this._measureSoon()
  }

  font(e) {
    const btn = e.target.closest("[data-font]")
    if (!btn) return
    this.state.style.font = btn.dataset.font
    this._renderFonts()
    this._paintStyle()
    this._syncHidden()
    this._measureSoon()
  }

  orient(e) {
    this._orientTouched = true
    this.state.style.orient = e.currentTarget.dataset.orient
    this._syncOrientSeg()
    this._paintStyle()
    this._syncHidden()
    this._measureSoon()
  }

  // "Surprise me" — random theme + color + orientation.
  shuffle() {
    const themes = this.vocabValue.themes || []
    const colors = this.vocabValue.colors || []
    if (themes.length) {
      const t = themes[Math.floor(Math.random() * themes.length)]
      this.state.style.theme = t.key
      this.state.style.layout = t.header
      this.state.style.font = t.font
      this.state.style.rateStyle = t.rateStyle
      this.state.style.graphic = t.graphic
      this.state.style.color = colors.length
        ? colors[Math.floor(Math.random() * colors.length)].hex
        : t.color
    }
    this._renderThemes()
    this._renderLayouts()
    this._renderColors()
    this._renderFonts()
    this._syncSegments()
    this._paintStyle()
    this._syncHidden()
    this._measureSoon()
  }

  // ---- Step 1: logo upload + crop --------------------------------------

  logoDragOver(e) {
    e.preventDefault()
    if (this.hasLogoDropTarget) this.logoDropTarget.classList.add("is-drag")
  }

  logoDragLeave() {
    if (this.hasLogoDropTarget) this.logoDropTarget.classList.remove("is-drag")
  }

  logoDrop(e) {
    e.preventDefault()
    if (this.hasLogoDropTarget) this.logoDropTarget.classList.remove("is-drag")
    const file = e.dataTransfer?.files?.[0]
    if (file) this._ingestLogo(file)
  }

  logoPaste(e) {
    const items = e.clipboardData?.items || []
    for (const it of items) {
      if (it.type && it.type.startsWith("image/")) {
        const file = it.getAsFile()
        if (file) { this._ingestLogo(file); break }
      }
    }
  }

  logoChange(e) {
    const file = e.target.files && e.target.files[0]
    if (file) this._ingestLogo(file)
  }

  logoFit(e) {
    this.state.style.logo.fit = e.currentTarget.dataset.fit
    this._syncSegments()
    this._paintLogo()
    this._syncHidden()
  }

  logoCrop(e) {
    const which = e.target.dataset.logo
    const v = parseInt(e.target.value, 10)
    if (which === "size") this.state.style.logo.size = v
    else this.state.style.logo[which] = v
    this._paintLogo()
    this._syncHidden()
    if (which === "size") this._measureSoon()
  }

  logoPlate(e) {
    this.state.style.logo.plate = e.currentTarget.dataset.plate
    this._syncSegments()
    this._paintLogo()
    this._syncHidden()
  }

  logoKnockout(e) {
    this.state.style.logo.knockout = e.target.checked
    this._paintLogo()
    this._syncHidden()
  }

  // Read a logo file → data URI; drop the sample, reveal the crop panel,
  // auto-suggest orientation from the logo aspect ratio, and paint it.
  _ingestLogo(file) {
    this._exitSample()
    const reader = new FileReader()
    reader.onload = () => {
      this.state.style.logo.dataUri = reader.result
      if (this.hasCropWrapTarget) this.cropWrapTarget.hidden = false
      // Also put the file on the real <input type=file> so it submits.
      this._attachLogoFile(file)
      // Auto-suggest landscape for wide logos (aspect >= 2), unless the
      // vendor already chose an orientation.
      const img = new Image()
      img.onload = () => {
        this._logoAspect = img.width / (img.height || 1)
        if (!this._orientTouched && this._logoAspect >= 2 && this.state.style.orient !== "landscape") {
          this.state.style.orient = "landscape"
          this._syncOrientSeg()
          this._paintStyle()
          this._syncHidden()
          this._measureSoon()
        }
      }
      img.src = reader.result
      this._paintLogo()
      this._measureSoon()
    }
    reader.readAsDataURL(file)
  }

  // A dropped/pasted file isn't on the <input type=file>; assign it via a
  // DataTransfer so the multipart form submits the logo.
  _attachLogoFile(file) {
    if (!this.hasLogoInputTarget) return
    try {
      const dt = new DataTransfer()
      dt.items.add(file)
      this.logoInputTarget.files = dt.files
    } catch (_) {
      // Some browsers block programmatic FileList assignment; the preview
      // still works, the file just won't upload on submit.
    }
  }

  // ---- preview: print / phone view -------------------------------------

  view(e) {
    const v = e.currentTarget.dataset.view
    e.currentTarget.parentElement
      .querySelectorAll("[data-view]")
      .forEach(b => b.classList.toggle("is-active", b === e.currentTarget))
    if (this.hasPreviewFrameTarget) this.previewFrameTarget.dataset.view = v
    this._measureSoon()
  }

  // ---- Step 5: review --------------------------------------------------

  agree(e) {
    const ok = e.target.checked
    if (this.hasSubmitBtnTarget) this.submitBtnTarget.disabled = !ok
    if (this.hasDownloadBtnTarget) this.downloadBtnTarget.disabled = !ok
  }

  submit() {
    if (this.hasAgreeTarget && !this.agreeTarget.checked) return
    this._syncHidden()
    if (this.hasFormTarget) this.formTarget.requestSubmit()
  }

  // Download and submit share the create path (the server redirects to the
  // brochure studio where the real PDF export lives). Both require the
  // authorization checkbox.
  download() {
    if (this.hasAgreeTarget && !this.agreeTarget.checked) return
    this._syncHidden()
    if (this.hasFormTarget) this.formTarget.requestSubmit()
  }

  // ---- sample handling --------------------------------------------------

  // On the first real edit, drop the demo sample and switch to the vendor's
  // own (initially empty) data.
  _exitSample() {
    if (!this.sampleValue) return
    this.sampleValue = false
    this._text("name", (this.state.org.name || "").trim() || "Your business name")
    this._contact("phone", this.state.org.phone)
    this._contact("email", this.state.org.email)
    this._contact("website", this.state.org.website)
    this._footer()
    this._subline()
    this._setCategory(this.state.org.category)
    this._paintLocations()
    this._paintServices()
    this._paintSections()
    this._paintLogo()
    if (this.hasBadgeTarget) this.badgeTarget.hidden = true
  }

  // ==== PAINTERS: state → embedded brochure (never rebuilds layout) ======

  // Full style application: data-attributes + CSS vars on the .vbr-doc.
  _paintStyle() {
    if (!this.doc) return
    const s = this.state.style
    const theme = (this.vocabValue.themes || []).find(t => t.key === s.theme)
    this.doc.dataset.theme = s.theme
    this.doc.dataset.layout = s.layout
    this.doc.dataset.orient = s.orient
    // rateStyle / graphic fall back to the theme's own when not overridden.
    this.doc.dataset.svcstyle = s.rateStyle || (theme && theme.rateStyle) || "rows"
    this.doc.dataset.graphic = s.graphic || (theme && theme.graphic) || "none"
    this.doc.style.setProperty("--brand", s.color)
    if (theme && theme.paper) this.doc.style.setProperty("--paper", theme.paper)
    const fam = this._fontFamily(s.font)
    if (fam) this.doc.style.setProperty("--head-font", fam)
    // preview frame aspect follows orientation
    if (this.hasPreviewFrameTarget) this.previewFrameTarget.dataset.orient = s.orient
    this._paintLogo()
  }

  // Logo: apply the data-URI via an inline STYLE OBJECT (never string
  // interpolation) + the crop/plate/knockout CSS vars.
  _paintLogo() {
    const el = this.doc && this.doc.querySelector('[data-vbr="logo"]')
    if (!el) return
    const lg = this.state.style.logo
    if (!lg.dataUri) {
      el.hidden = true
      el.style.backgroundImage = ""
      return
    }
    el.hidden = false
    // data-URI set through the style property object — no markup interpolation.
    el.style.backgroundImage = `url("${lg.dataUri}")`

    const size = lg.fit === "crop" ? `${lg.zoom}%` : (lg.fit === "cover" ? "cover" : "contain")
    const pos = lg.fit === "crop" ? `${lg.x}% ${lg.y}%` : "center"
    const plate = lg.plate === "accent" ? this.state.style.color
      : (lg.plate === "white" ? "#ffffff" : "transparent")
    this.doc.style.setProperty("--logo-fit", size)
    this.doc.style.setProperty("--logo-pos", pos)
    this.doc.style.setProperty("--logo-plate", plate)
    this.doc.style.setProperty("--logo-blend", lg.knockout ? "multiply" : "normal")
    this.doc.style.setProperty("--logo-scale", String((lg.size || 100) / 100))
  }

  _paintLocations() {
    const box = this.doc && this.doc.querySelector('[data-vbr="locations"]')
    const row = this.doc && this.doc.querySelector('[data-vbr-row="locations"]')
    const lines = [this.state.org.address.trim(), ...this.state.locations].filter(Boolean)
    if (box) box.innerHTML = lines.map(l => `<span class="vbr-loc">${this._esc(l)}</span>`).join("")
    if (row) row.hidden = lines.length === 0
  }

  _paintServices() {
    const box = this.doc && this.doc.querySelector('[data-vbr="services"]')
    if (!box) return
    const rows = this.state.services.filter(s => s.name)
    if (!rows.length) {
      box.innerHTML = `<div class="vbr-svc-flow"><div class="vbr-service"><span class="vbr-service-name">Your services will appear here</span></div></div>`
      return
    }
    // Emit BOTH the flow markup (rows/cards/twoCol) and the table body, just
    // like the server render — CSS shows the active one per data-svcstyle.
    const flow = rows.map(r => {
      const loc = this._modeLabel(r.mode)
      const cost = this._costDisplay(r.rate, r.unit) || "Contact for pricing"
      return `<div class="vbr-service">` +
        `<span class="vbr-service-name">${this._esc(r.name)}</span>` +
        (loc ? `<span class="vbr-service-loc">${this._esc(loc)}</span>` : "") +
        `<span class="vbr-service-cost">${this._esc(cost)}</span>` +
        `</div>`
    }).join("")
    const body = rows.map(r => {
      const cost = this._costDisplay(r.rate, r.unit) || "Contact for pricing"
      return `<tr>` +
        `<td class="vbr-td-name">${this._esc(r.name)}</td>` +
        `<td class="vbr-td-mode">${this._esc(this._modeLabel(r.mode))}</td>` +
        `<td class="vbr-td-rate">${this._esc(cost)}</td>` +
        `</tr>`
    }).join("")
    box.innerHTML =
      `<div class="vbr-svc-flow">${flow}</div>` +
      `<table class="vbr-svc-table">` +
      `<thead><tr><th>Service</th><th>Mode</th><th class="vbr-t-rate">Rate</th></tr></thead>` +
      `<tbody>${body}</tbody></table>`
  }

  _paintSections() {
    const box = this.doc && this.doc.querySelector('[data-vbr="sections"]')
    if (!box) return
    const sec = this.state.sections
    const en = sec.enabled || {}
    const notes = (this.state.org.notes || "").trim()
    const hours = (sec.hours || "").trim()
    const area = (sec.area || "").trim()
    const amin = (sec.ages_min || "").toString().trim()
    const amax = (sec.ages_max || "").toString().trim()
    const funding = Array.isArray(sec.funding) ? sec.funding.filter(Boolean) : []

    const showAbout = en.about && notes
    const showHours = en.hours && hours
    const showArea = en.area && area
    const showAges = en.ages && (amin || amax)
    const showFunding = en.funding && funding.length

    let ages = ""
    if (amin && amax) ages = `Ages ${amin}–${amax}`
    else if (amin) ages = `Ages ${amin}+`
    else if (amax) ages = `Through age ${amax}`

    let html = ""
    if (showAbout) {
      html += `<div class="vbr-block vbr-sec-about" data-vbr-sec="about">` +
        `<h2 class="vbr-h2">About</h2><p class="vbr-about">${this._esc(notes)}</p></div>`
    }
    let grid = ""
    if (showHours) grid += `<div class="vbr-block vbr-sec-hours" data-vbr-sec="hours"><h2 class="vbr-h2">Hours</h2><p class="vbr-secval">${this._esc(hours)}</p></div>`
    if (showAges) grid += `<div class="vbr-block vbr-sec-ages" data-vbr-sec="ages"><h2 class="vbr-h2">Ages served</h2><p class="vbr-secval">${this._esc(ages)}</p></div>`
    if (showArea) grid += `<div class="vbr-block vbr-sec-area" data-vbr-sec="area"><h2 class="vbr-h2">Service area</h2><p class="vbr-secval">${this._esc(area)}</p></div>`
    if (showFunding) {
      const chips = funding.map(f => `<span class="vbr-chip">${this._esc(f)}</span>`).join("")
      grid += `<div class="vbr-block vbr-sec-funding" data-vbr-sec="funding"><h2 class="vbr-h2">Funding accepted</h2><div class="vbr-chips">${chips}</div></div>`
    }
    if (grid) html += `<div class="vbr-secgrid">${grid}</div>`
    box.innerHTML = html
  }

  // The subline is the tagline if present, else the joined distinct modes,
  // else the category.
  _subline() {
    const tag = (this.state.org.tagline || "").trim()
    let sub = tag
    if (!sub) {
      const modes = []
      this.state.services.forEach(s => {
        const m = this._modeLabel(s.mode)
        if (m && !modes.includes(m)) modes.push(m)
      })
      sub = modes.length ? modes.slice(0, 4).join(" · ") : (this.state.org.category || "").trim()
    }
    this._text("subline", sub)
    const row = this.doc && this.doc.querySelector('[data-vbr-row="subline"]')
    if (row) row.hidden = !sub
  }

  _footer() {
    const web = (this.state.org.website || "").trim()
    const eml = (this.state.org.email || "").trim()
    this._text("footerweb", web || eml || "")
  }

  _setCategory(name) {
    if (!this.doc) return
    let slug = String(name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")
    if (!slug) slug = "default"
    this.doc.dataset.category = slug
  }

  // ==== fit engine =======================================================

  _measureSoon() {
    if (this._raf) cancelAnimationFrame(this._raf)
    this._raf = requestAnimationFrame(() => this._measure())
  }

  // Compare the doc's natural scrollHeight to the one-page ideal for the
  // current orientation (width * 11/8.5 portrait, width * 8.5/11 landscape).
  _measure() {
    if (!this.doc || !this.hasFitTarget) return
    const w = this.doc.scrollWidth || this.doc.offsetWidth
    if (!w) return
    const landscape = this.state.style.orient === "landscape"
    const ideal = landscape ? w * (8.5 / 11) : w * (11 / 8.5)
    const actual = this.doc.scrollHeight
    const over = actual > ideal + 2   // small tolerance for sub-pixel rounding
    this.fitTarget.classList.toggle("is-ok", !over)
    this.fitTarget.classList.toggle("is-over", over)
    if (this.hasFitTextTarget) {
      this.fitTextTarget.textContent = over
        ? "Runs past one page — trim a section"
        : "Fits one page"
    }
    this._fits = !over
    if ((this.stepValue || 1) === 5) this._refreshChecklist()
  }

  // ==== review checklist =================================================

  _refreshChecklist() {
    if (!this.hasChecklistTarget) return
    const has = {
      name: !!(this.state.org.name || "").trim(),
      contact: !!((this.state.org.phone || "").trim() || (this.state.org.email || "").trim() || (this.state.org.website || "").trim()),
      services: this.state.services.some(s => s.name),
      fits: this._fits !== false
    }
    this.checklistTarget.querySelectorAll("[data-check]").forEach(li => {
      li.classList.toggle("is-ok", !!has[li.dataset.check])
    })
  }

  // ==== hidden-field sync ================================================

  _syncHidden() {
    const s = this.state.style
    const lg = s.logo
    if (this.hasThemeFieldTarget) this.themeFieldTarget.value = s.theme
    if (this.hasColorFieldTarget) this.colorFieldTarget.value = s.color
    if (this.hasLayoutFieldTarget) this.layoutFieldTarget.value = s.layout
    if (this.hasOrientationFieldTarget) this.orientationFieldTarget.value = s.orient
    if (this.hasFontFieldTarget) this.fontFieldTarget.value = s.font
    if (this.hasLogoScaleFieldTarget) this.logoScaleFieldTarget.value = String((lg.size || 100) / 100)

    const styleOut = {
      fit: lg.fit, zoom: lg.zoom, x: lg.x, y: lg.y,
      plate: lg.plate, knockout: lg.knockout
    }
    if (s.rateStyle) styleOut.rateStyle = s.rateStyle
    if (s.graphic) styleOut.graphic = s.graphic
    if (this.hasStyleFieldTarget) this.styleFieldTarget.value = JSON.stringify(styleOut)

    const sec = this.state.sections
    const sectionsOut = {
      hours: sec.hours || "",
      ages_min: sec.ages_min || "",
      ages_max: sec.ages_max || "",
      area: sec.area || "",
      funding: Array.isArray(sec.funding) ? sec.funding : [],
      enabled: sec.enabled || {}
    }
    if (this.hasSectionsFieldTarget) this.sectionsFieldTarget.value = JSON.stringify(sectionsOut)

    const locs = [this.state.org.address.trim(), ...this.state.locations].filter(Boolean)
    if (this.hasLocationsFieldTarget) this.locationsFieldTarget.value = JSON.stringify(locs)
  }

  // ==== picker rendering (client-side, from vocabValue) =================

  _renderThemes() {
    if (!this.hasThemesTarget) return
    const themes = this.vocabValue.themes || []
    const active = this.state.style.theme
    this.themesTarget.innerHTML = themes.map(t => `
      <button type="button" class="vbw-theme${t.key === active ? " is-active" : ""}"
              data-theme="${t.key}" data-action="vendor-intake#theme" title="${this._esc(t.mood)}">
        <span class="vbw-theme-swatch" style="background:${t.color}"></span>
        <span class="vbw-theme-meta">
          <span class="vbw-theme-name">${this._esc(t.name)}</span>
          <span class="vbw-theme-mood">${this._esc(t.mood)}</span>
        </span>
      </button>`).join("")
  }

  _renderLayouts() {
    if (!this.hasLayoutsTarget) return
    const layouts = this.vocabValue.layouts || []
    const active = this.state.style.layout
    this.layoutsTarget.innerHTML = layouts.map(l => `
      <button type="button" class="vbw-pill${l.key === active ? " is-active" : ""}"
              data-layout="${l.key}" data-action="vendor-intake#layout">${this._esc(l.name)}</button>`).join("")
  }

  _renderColors() {
    if (!this.hasColorsTarget) return
    const colors = this.vocabValue.colors || []
    const active = (this.state.style.color || "").toLowerCase()
    this.colorsTarget.innerHTML = colors.map(c => `
      <button type="button" class="vbw-swatch${c.hex.toLowerCase() === active ? " is-active" : ""}"
              data-color="${c.hex}" data-action="vendor-intake#color"
              style="background:${c.hex}" title="${this._esc(c.name)}"></button>`).join("")
  }

  _renderFonts() {
    if (!this.hasFontsTarget) return
    const fonts = this.vocabValue.fonts || []
    const active = this.state.style.font
    this.fontsTarget.innerHTML = fonts.map(f => `
      <button type="button" class="vbw-fontbtn${f.key === active ? " is-active" : ""}"
              data-font="${f.key}" data-action="vendor-intake#font"
              style="font-family:${f.family}">${this._esc(f.name)}</button>`).join("")
  }

  _syncSegments() {
    // Fit segment (contain/cover/crop)
    this.element.querySelectorAll("[data-fit]").forEach(b => {
      b.classList.toggle("is-active", b.dataset.fit === this.state.style.logo.fit)
    })
    // Plate segment (transparent/white/accent)
    this.element.querySelectorAll("[data-plate]").forEach(b => {
      b.classList.toggle("is-active", b.dataset.plate === this.state.style.logo.plate)
    })
  }

  _syncOrientSeg() {
    if (this.hasOrientSegTarget) {
      this.orientSegTarget.querySelectorAll("[data-orient]").forEach(b => {
        b.classList.toggle("is-active", b.dataset.orient === this.state.style.orient)
      })
    }
    if (this.hasOrientHintTarget) {
      this.orientHintTarget.textContent = this.state.style.orient === "landscape"
        ? "Landscape suits wide logos and side-by-side content."
        : "Portrait is the standard one-page handout."
    }
  }

  // ==== helpers ==========================================================

  _normSections(raw) {
    const s = this._obj(raw)
    const en = this._obj(s.enabled)
    return {
      hours: s.hours || "",
      ages_min: s.ages_min || "",
      ages_max: s.ages_max || "",
      area: s.area || "",
      funding: Array.isArray(s.funding) ? s.funding : [],
      enabled: {
        about: this._bool(en.about),
        hours: this._bool(en.hours),
        ages: this._bool(en.ages),
        area: this._bool(en.area),
        funding: this._bool(en.funding)
      }
    }
  }

  _modeLabel(key) {
    const m = (this.vocabValue.modes || []).find(x => x.key === key)
    return m ? m.name : ""
  }

  _fontFamily(key) {
    const f = (this.vocabValue.fonts || []).find(x => x.key === key)
    return f ? f.family : ""
  }

  _costDisplay(rate, unit) {
    const n = parseFloat(rate)
    if (!(n >= 0) || String(rate).trim() === "") return ""
    const u = (this.vocabValue.units || []).find(x => x.key === unit)
    const label = u ? u.label : ""
    const suffix = (!label || label === "flat") ? "" : `/${label}`
    return `$${n}${suffix}`
  }

  _text(key, val) {
    const el = this.doc && this.doc.querySelector(`[data-vbr="${key}"]`)
    if (el) el.textContent = val
  }

  _contact(key, val) {
    this._text(key, val)
    const row = this.doc && this.doc.querySelector(`[data-vbr-row="${key}"]`)
    if (row) row.hidden = !String(val).trim()
  }

  _obj(v) {
    if (v && typeof v === "object" && !Array.isArray(v)) return v
    if (typeof v === "string") { try { const p = JSON.parse(v); return (p && typeof p === "object") ? p : {} } catch (_) { return {} } }
    return {}
  }

  _bool(v) { return v === true || v === "true" || v === "1" || v === 1 }

  _num(v, d) { const n = parseInt(v, 10); return Number.isFinite(n) ? n : d }

  _esc(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }
}
